<script setup>
import { computed, onMounted, ref } from "vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";

const props = defineProps({
  store: {
    type: Object,
    required: true
  }
});

const modalRef = ref(null);
const selectedImage = ref(null);
const continuation = computed(() => props.store.getters["canva/continuation"]);
const designs = computed(() => props.store.getters["canva/designs"]);
const error = computed(() => props.store.getters["canva/error"]);
const loading = computed(() => props.store.getters["canva/loading"]);
const uploading = computed(() => props.store.getters["canva/uploading"]);
const disabled = computed(
  () => !selectedImage.value || error.value || loading.value || uploading.value
);

const moreButtonText = computed(() => {
  if (loading.value && designs.value.length) {
    return "Loading...";
  }

  return "Load more...";
});

const submitButtonText = computed(() => {
  if (uploading.value) {
    return "Uploading";
  }

  return "Proceed";
});

const handleDesignSelect = async desingId => {
  props.store.dispatch("canva/selectDesign", desingId);
};

const fetchDesigns = () => {
  props.store.dispatch("canva/fetchDesigns", continuation.value);
};

const getDesignAlt = design => design?.title || "Untitled Design";

const getDesignSrc = design => design?.thumbnail?.url;

onMounted(() => {
  props.store.commit("canva/$setModal", modalRef.value);
  fetchDesigns();
});
</script>

<template>
  <div ref="modalRef" class="canva-modal modal" @click.stop>
    <div class="canva-modal__modal-box">
      <div class="canva-modal__modal-header">
        <div class="canva-modal__header">
          <h3 class="canva-modal__title">Choose image you want to upload:</h3>
          <button class="canva-modal__modal-close" type="button" data-dismiss="modal">
            <UseSvg icon="modal-close" />
          </button>
        </div>
      </div>

      <div class="canva-modal__modal-body">
        <div v-if="loading && !designs.length" class="canva-modal__loading">Loading...</div>
        <div v-else-if="error" class="canva-modal__error">{{ error }}</div>
        <div v-else-if="!loading && !designs.length">No images found</div>

        <div
          v-else
          class="canva-modal__gallery"
          :class="{ 'canva-modal__gallery--uploading': uploading }"
        >
          <a
            v-for="design in designs"
            :key="design.id"
            :class="{ 'canva-modal__gallery-item--selected': design.id === selectedImage }"
            class="canva-modal__gallery-item"
            href="javascript:void(0);"
            @click="selectedImage = design.id"
          >
            <img
              :alt="getDesignAlt(design)"
              :class="{ 'canva-modal__gallery-image--placeholder': !getDesignSrc(design) }"
              class="canva-modal__gallery-image"
              :src="getDesignSrc(design)"
            />
            <UseSvg
              v-if="design.id === selectedImage"
              icon="check-circle-bold"
              class="canva-modal__gallery-check"
            />
          </a>
        </div>
      </div>

      <div class="canva-modal__actions">
        <button
          v-if="continuation"
          :disabled="loading || uploading"
          class="canva-modal__action canva-modal__action--secondary"
          type="button"
          @click="fetchDesigns()"
        >
          {{ moreButtonText }}
        </button>
        <button
          :disabled="disabled"
          class="canva-modal__action canva-modal__action--primary"
          type="button"
          @click="handleDesignSelect(selectedImage)"
        >
          {{ submitButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/modal";
@import "frontend/init/jiffy/button/mixin";

.canva-modal {
  $this: &;

  @include modal;

  top: unset;
  padding: unset !important;
  color: $black;
  cursor: default;
  z-index: 2147483642; // override chat & feedback triggers

  &--made {
    #{$this}__title {
      font-size: 17px;
      font-weight: 500;
      letter-spacing: -0.4px;
    }

    #{$this}__action {
      font-weight: 600;
    }
  }

  &--dtf {
    max-height: 100dvh;
    overflow: hidden;
    top: 0;

    #{$this}__modal-box {
      top: 5%;

      @include with-tablet {
        top: 10%;
      }
    }

    #{$this}__modal-body,
    #{$this}__gallery {
      max-height: calc(100vh - 250px);

      @include with-tablet {
        max-height: calc(50vh - 30px);
      }
    }

    #{$this}__modal-body {
      overflow-y: hidden;
    }

    #{$this}__gallery {
      overflow-y: auto;
    }

    @include with-tablet {
      max-height: 90%;
    }

    @include with-desktop {
      max-height: calc(100% - 30px);
    }
  }

  @include with-tablet {
    top: 0;
  }

  &__modal-box {
    top: 0;
    padding: 0 12px 24px;
    border: 0;
    border-radius: 24px;
    transform: none;

    @include with-tablet {
      top: 10%;
      padding-right: 16px;
      padding-left: 16px;
      border-radius: 16px;
    }

    @include with-desktop {
      max-width: 850px;
    }
  }

  &__title {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 24px 0 30px;
    font-size: 18px;
    letter-spacing: -0.3px;
  }

  &__modal-header {
    position: relative;
    min-height: 24px;
    font-weight: 700;
  }

  &__modal-close {
    @include clear-button;
    @include svg-wrapper;

    top: 24px;
    right: 10px;
    width: 23px;
    height: 23px;

    &:focus,
    &:hover {
      opacity: 0.7;
    }
  }

  &__gallery {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    max-height: 400px;
    overflow-y: auto;

    @include with-desktop {
      grid-template-columns: repeat(6, 1fr);
    }

    &--uploading {
      & > :not(.canva-modal__gallery-item--selected) {
        filter: grayscale(1);
        opacity: 0.25;
      }
    }
  }

  &__gallery-item {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    transition:
      filter 5s,
      opacity 5s;
    color: $black;
    filter: grayscale(0);
    opacity: 1;

    &--selected {
      &::before {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border: 3px solid $black;
        content: "";
      }

      &::after {
        position: absolute;
        content: "";
      }
    }
  }

  &__gallery-image {
    width: 100%;
    height: 100%;
    object-fit: cover;

    &--placeholder {
      display: flex;
      justify-content: center;
      align-items: center;
      background: url(remote-image("gen/placeholder.svg")) no-repeat center;
      background-size: 30%;
    }
  }

  &__gallery-check {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 27px;
    height: 27px;
    border: 1px solid $white;
    border-radius: 50%;
    background-color: $white;
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 20px 20px 0;
    text-align: center;
  }

  &__action {
    width: 100%;
    max-width: 400px;
    height: 64px;
    border: 0;
    border-radius: 8px;
    font-size: 20px;
    font-weight: 700;

    &--primary {
      background-color: $black-light;
      color: $white;

      &:focus:not(:disabled),
      &:hover:not(:disabled) {
        background-color: $black;
      }

      &:disabled {
        opacity: 0.5;
      }
    }

    &--secondary {
      border: 1px solid $black-light;
      background-color: transparent;
      color: $black;

      &:focus:not(:disabled),
      &:hover:not(:disabled) {
        background-color: $black;
        color: $white;
      }

      &:disabled {
        border-color: $gray-border-light;
        background-color: $gray-border-light;
        color: $warm-grey;
      }
    }
  }
}
</style>
